/**
 * NotificationList Page - Shows all notifications/alerts in a list view
 * Allows filtering, marking as read, and navigating to details
 * Enhanced with real-time updates and engagement tracking
 */

import { useState, useEffect, useCallback } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import {
  Bell,
  Eye,
  TrendingUp,
  Radio,
  Target,
  Calendar,
  ArrowUp,
  ArrowDown,
  User,
  AlertTriangle,
  Briefcase,
  CheckCheck,
  Settings,
  Filter,
  RefreshCw,
  ChevronRight,
  BarChart2
} from 'lucide-react';
import alertService, { ALERT_TYPE_CONFIG, PRIORITY_LABELS } from '../../services/alertService';
import { useAlerts, useRealtimeAlerts, useAlertStats } from '../../hooks/useAlerts';
import { formatTimeAgo, groupAlertsByDate, filterAlerts, sortAlerts } from '../../utils/alertHelpers';

// Icon mapping for alert types
const ICON_MAP = {
  eye: Eye,
  'trending-up': TrendingUp,
  radio: Radio,
  target: Target,
  calendar: Calendar,
  'arrow-up': ArrowUp,
  'arrow-down': ArrowDown,
  user: User,
  'alert-triangle': AlertTriangle,
  briefcase: Briefcase,
  bell: Bell
};

// Color classes for alert type badges
const TYPE_COLORS = {
  blue: 'bg-blue-100 text-blue-700',
  green: 'bg-green-100 text-green-700',
  purple: 'bg-purple-100 text-purple-700',
  orange: 'bg-orange-100 text-orange-700',
  pink: 'bg-pink-100 text-pink-700',
  red: 'bg-red-100 text-red-700',
  yellow: 'bg-yellow-100 text-yellow-700',
  indigo: 'bg-indigo-100 text-indigo-700',
  gray: 'bg-gray-100 text-gray-700'
};

// Priority badge colors
const PRIORITY_BADGE_COLORS = {
  low: 'bg-gray-100 text-gray-600',
  medium: 'bg-blue-100 text-blue-600',
  high: 'bg-orange-100 text-orange-600',
  critical: 'bg-red-100 text-red-600'
};

export default function NotificationList() {
  const navigate = useNavigate();
  const [filter, setFilter] = useState('all'); // all, unread, high_priority
  const [typeFilter, setTypeFilter] = useState('all');
  const [sortBy, setSortBy] = useState('date');
  const [showStats, setShowStats] = useState(false);

  // Use the enhanced alerts hook - disable autoFetch since we handle it manually
  const {
    alerts,
    loading,
    error,
    totalCount,
    fetchAlerts,
    markAsRead,
    markAllAsRead,
    dismissAlert,
    refresh
  } = useAlerts({
    autoFetch: false,
    realtime: false,
    limit: 100
  });

  // Use alert stats hook
  const { stats, loading: statsLoading } = useAlertStats();

  // Fetch on mount and when filters change
  useEffect(() => {
    const options = { limit: 100 };
    if (filter === 'unread') {
      options.unreadOnly = true;
    }
    if (filter === 'high_priority') {
      options.minPriority = 3;
    }
    if (typeFilter !== 'all') {
      options.alertType = typeFilter;
    }
    fetchAlerts(options);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filter, typeFilter]);

  // Apply local filtering and sorting
  const filteredAlerts = filterAlerts(alerts, {
    unreadOnly: filter === 'unread',
    alertType: typeFilter !== 'all' ? typeFilter : null,
    minPriority: filter === 'high_priority' ? 3 : null
  });

  const sortedAlerts = sortAlerts(filteredAlerts, sortBy, 'desc');

  // Handle mark all as read
  const handleMarkAllRead = async () => {
    await markAllAsRead();
  };

  // Handle alert click with tracking
  const handleAlertClick = async (alert) => {
    if (!alert.is_read) {
      try {
        await alertService.trackClick(alert.id);
      } catch (err) {
        console.error('Error tracking click:', err);
      }
    }
    navigate(`/seller/alert/${alert.id}`);
  };

  // Get icon component for alert type
  const getIcon = (alert) => {
    const config = ALERT_TYPE_CONFIG[alert.alert_type] || { icon: 'bell' };
    return ICON_MAP[config.icon] || Bell;
  };

  // Get color class for alert type
  const getTypeColor = (alert) => {
    const config = ALERT_TYPE_CONFIG[alert.alert_type] || { color: 'gray' };
    return TYPE_COLORS[config.color] || TYPE_COLORS.gray;
  };

  // Get type label
  const getTypeLabel = (alert) => {
    const config = ALERT_TYPE_CONFIG[alert.alert_type] || { label: alert.alert_type };
    return config.label;
  };

  const unreadCount = sortedAlerts.filter(a => !a.is_read).length;

  return (
    <div className="max-w-4xl mx-auto">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Notifications</h1>
          <p className="text-gray-500 mt-1">
            {totalCount} total notifications, {unreadCount} unread
          </p>
        </div>
        <div className="flex items-center gap-3">
          <button
            onClick={() => setShowStats(!showStats)}
            className={`p-2 rounded-lg transition-colors ${
              showStats ? 'text-purple-600 bg-purple-100' : 'text-gray-500 hover:text-gray-700 hover:bg-gray-100'
            }`}
            title="Show Statistics"
          >
            <BarChart2 className="w-5 h-5" />
          </button>
          <button
            onClick={refresh}
            className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
            title="Refresh"
          >
            <RefreshCw className="w-5 h-5" />
          </button>
          <Link
            to="/seller/notifications/settings"
            className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
            title="Settings"
          >
            <Settings className="w-5 h-5" />
          </Link>
        </div>
      </div>

      {/* Statistics Panel */}
      {showStats && stats && (
        <div className="bg-white rounded-lg border border-gray-200 p-4 mb-6">
          <h3 className="text-sm font-semibold text-gray-700 mb-3">30-Day Statistics</h3>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="text-center">
              <div className="text-2xl font-bold text-gray-900">{stats.total}</div>
              <div className="text-xs text-gray-500">Total Alerts</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-purple-600">{stats.unread}</div>
              <div className="text-xs text-gray-500">Unread</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-green-600">
                {stats.total > 0 ? Math.round((stats.read / stats.total) * 100) : 0}%
              </div>
              <div className="text-xs text-gray-500">Read Rate</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-orange-600">{stats.highPriority}</div>
              <div className="text-xs text-gray-500">High Priority</div>
            </div>
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="bg-white rounded-lg border border-gray-200 p-4 mb-6">
        <div className="flex flex-wrap items-center gap-4">
          <div className="flex items-center gap-2">
            <Filter className="w-4 h-4 text-gray-500" />
            <span className="text-sm font-medium text-gray-700">Filter:</span>
          </div>

          <select
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            className="text-sm border-gray-300 rounded-lg focus:ring-purple-500 focus:border-purple-500"
          >
            <option value="all">All Notifications</option>
            <option value="unread">Unread Only</option>
            <option value="high_priority">High Priority</option>
          </select>

          <select
            value={typeFilter}
            onChange={(e) => setTypeFilter(e.target.value)}
            className="text-sm border-gray-300 rounded-lg focus:ring-purple-500 focus:border-purple-500"
          >
            <option value="all">All Types</option>
            <option value="profile_view">Profile Views</option>
            <option value="interest_spike">Interest Spikes</option>
            <option value="new_signal">New Signals</option>
            <option value="match_score">Match Scores</option>
            <option value="meeting_request">Meeting Requests</option>
          </select>

          {unreadCount > 0 && (
            <button
              onClick={handleMarkAllRead}
              className="ml-auto text-sm text-purple-600 hover:text-purple-800 flex items-center gap-1"
            >
              <CheckCheck className="w-4 h-4" />
              Mark all as read
            </button>
          )}
        </div>
      </div>

      {/* Notification List */}
      <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600"></div>
          </div>
        ) : sortedAlerts.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-12 text-gray-500">
            <Bell className="w-12 h-12 mb-3 text-gray-300" />
            <p className="text-lg font-medium">No notifications</p>
            <p className="text-sm text-gray-400 mt-1">
              {filter !== 'all' ? 'Try changing your filters' : 'Check back later for updates'}
            </p>
          </div>
        ) : (
          <ul className="divide-y divide-gray-100">
            {sortedAlerts.map((alert) => {
              const IconComponent = getIcon(alert);
              const typeColor = getTypeColor(alert);
              const typeLabel = getTypeLabel(alert);
              const priorityLabel = PRIORITY_LABELS[alert.priority] || 'medium';

              return (
                <li
                  key={alert.id}
                  onClick={() => handleAlertClick(alert)}
                  className={`relative px-6 py-4 cursor-pointer transition-colors hover:bg-gray-50 ${
                    !alert.is_read ? 'bg-purple-50' : ''
                  }`}
                >
                  <div className="flex items-start gap-4">
                    {/* Icon */}
                    <div className={`flex-shrink-0 w-10 h-10 rounded-full ${typeColor} flex items-center justify-center`}>
                      <IconComponent className="w-5 h-5" />
                    </div>

                    {/* Content */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between gap-4">
                        <div>
                          <h3 className={`text-base ${!alert.is_read ? 'font-semibold' : 'font-medium'} text-gray-900`}>
                            {alert.title}
                          </h3>
                          <p className="text-sm text-gray-500 mt-1 line-clamp-2">
                            {alert.description}
                          </p>
                        </div>
                        <ChevronRight className="flex-shrink-0 w-5 h-5 text-gray-400" />
                      </div>

                      <div className="flex items-center gap-3 mt-2">
                        {/* Type badge */}
                        <span className={`inline-flex items-center px-2 py-0.5 text-xs font-medium rounded ${typeColor}`}>
                          {typeLabel}
                        </span>

                        {/* Priority badge (for high/critical) */}
                        {alert.priority >= 3 && (
                          <span className={`inline-flex items-center px-2 py-0.5 text-xs font-medium rounded ${PRIORITY_BADGE_COLORS[priorityLabel]}`}>
                            {priorityLabel.charAt(0).toUpperCase() + priorityLabel.slice(1)}
                          </span>
                        )}

                        {/* Buyer name if available */}
                        {alert.buyerName && (
                          <span className="text-xs text-gray-500">
                            {alert.buyerName}
                          </span>
                        )}

                        {/* Time */}
                        <span className="text-xs text-gray-400 ml-auto">
                          {formatTimeAgo(alert.created_at)}
                        </span>
                      </div>
                    </div>

                    {/* Unread indicator */}
                    {!alert.is_read && (
                      <div className="absolute left-2 top-1/2 -translate-y-1/2 w-2 h-2 rounded-full bg-purple-600"></div>
                    )}
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </div>
  );
}
